import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, CheckCircle2, XCircle, AlertTriangle, ExternalLink, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  {
    id: 'backend',
    name: 'Backend API',
    description: 'FastAPI application server handling all API requests',
    checkUrl: `${API}/`,
    statusPage: null,
    icon: '🖥️',
  },
  {
    id: 'mongodb',
    name: 'MongoDB',
    description: 'Database storing products, orders, customers, scans, and price alerts',
    checkUrl: `${API}/products`,
    statusPage: null,
    icon: '🗄️',
  },
  {
    id: 'openai',
    name: 'OpenAI GPT-4o',
    description: 'AI service powering Ted AI bag scanning, dog matching, and price search',
    checkUrl: `${API}/admin/service-check/openai`,
    statusPage: 'https://status.openai.com',
    icon: '🤖',
    requiresAuth: true,
  },
  {
    id: 'resend',
    name: 'Resend (Email)',
    description: 'Email delivery for order notifications, password resets, and price alerts',
    checkUrl: `${API}/admin/service-check/resend`,
    statusPage: 'https://resend-status.com',
    icon: '📧',
    requiresAuth: true,
  },
  {
    id: 'shopify',
    name: 'Shopify (Checkout)',
    description: 'Checkout redirect for sample box purchases via Shopify cart links',
    checkUrl: null,
    statusPage: 'https://www.shopifystatus.com',
    icon: '🛒',
    alwaysUp: true,
  },
];

const StatusBadge = ({ status }) => {
  if (status === 'checking') return (
    <span className="flex items-center gap-1.5 text-xs font-semibold text-[#999]">
      <RefreshCw className="w-3.5 h-3.5 animate-spin" /> Checking...
    </span>
  );
  if (status === 'ok') return (
    <span className="flex items-center gap-1.5 text-xs font-semibold text-green-600">
      <CheckCircle2 className="w-3.5 h-3.5" /> Operational
    </span>
  );
  if (status === 'degraded') return (
    <span className="flex items-center gap-1.5 text-xs font-semibold text-amber-600">
      <AlertTriangle className="w-3.5 h-3.5" /> Degraded
    </span>
  );
  if (status === 'down') return (
    <span className="flex items-center gap-1.5 text-xs font-semibold text-red-600">
      <XCircle className="w-3.5 h-3.5" /> Down
    </span>
  );
  return (
    <span className="flex items-center gap-1.5 text-xs font-semibold text-[#999]">
      <Clock className="w-3.5 h-3.5" /> Unknown
    </span>
  );
};

export const ServiceStatus = () => {
  const [statuses, setStatuses] = useState({});
  const [details, setDetails] = useState({});
  const [lastChecked, setLastChecked] = useState(null);
  const [checking, setChecking] = useState(false);

  const getAuthHeader = () => {
    const token = localStorage.getItem('admin_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const checkService = useCallback(async (service) => {
    if (service.alwaysUp) {
      return { status: 'ok', detail: 'External service — check status page for issues' };
    }
    if (!service.checkUrl) {
      return { status: 'unknown', detail: 'No health check available' };
    }
    try {
      const headers = service.requiresAuth ? getAuthHeader() : {};
      const start = Date.now();
      const response = await axios.get(service.checkUrl, { headers, timeout: service.id === 'openai' ? 20000 : 10000 });
      const latency = Date.now() - start;
      if (response.status === 200) {
        const detail = response.data?.detail || response.data?.message || `${latency}ms response time`;
        return { status: latency > 5000 ? 'degraded' : 'ok', detail: typeof detail === 'string' ? detail : `${latency}ms` };
      }
      return { status: 'degraded', detail: `HTTP ${response.status}` };
    } catch (error) {
      const msg = error.response?.data?.detail || error.message || 'Connection failed';
      return { status: 'down', detail: typeof msg === 'string' ? msg : 'Service unavailable' };
    }
  }, []);

  const checkAll = useCallback(async () => {
    setChecking(true);
    const newStatuses = {};
    const newDetails = {};

    // Set all to checking
    SERVICES.forEach(s => { newStatuses[s.id] = 'checking'; });
    setStatuses({ ...newStatuses });

    // Check all in parallel
    const results = await Promise.all(
      SERVICES.map(async (service) => {
        const result = await checkService(service);
        return { id: service.id, ...result };
      })
    );

    results.forEach(r => {
      newStatuses[r.id] = r.status;
      newDetails[r.id] = r.detail;
    });

    setStatuses(newStatuses);
    setDetails(newDetails);
    setLastChecked(new Date());
    setChecking(false);
  }, [checkService]);

  useEffect(() => {
    checkAll();
    const interval = setInterval(checkAll, 60000);
    return () => clearInterval(interval);
  }, [checkAll]);

  const allOk = Object.values(statuses).every(s => s === 'ok');
  const anyDown = Object.values(statuses).some(s => s === 'down');

  return (
    <div className="space-y-6" data-testid="service-status">
      {/* Overall status */}
      <div className={`rounded-2xl p-6 text-center ${
        anyDown ? 'bg-red-50 border-2 border-red-200' :
        allOk ? 'bg-green-50 border-2 border-green-200' :
        'bg-amber-50 border-2 border-amber-200'
      }`}>
        <div className="flex items-center justify-center gap-2 mb-2">
          {anyDown ? <XCircle className="w-6 h-6 text-red-600" /> :
           allOk ? <CheckCircle2 className="w-6 h-6 text-green-600" /> :
           <AlertTriangle className="w-6 h-6 text-amber-600" />}
          <h3 className={`text-lg font-bold ${
            anyDown ? 'text-red-700' : allOk ? 'text-green-700' : 'text-amber-700'
          }`}>
            {anyDown ? 'Service Issues Detected' : allOk ? 'All Systems Operational' : 'Checking Services...'}
          </h3>
        </div>
        <p className="text-sm text-[#666]">
          {lastChecked ? `Last checked: ${lastChecked.toLocaleTimeString('en-GB')}` : 'Checking...'}
          {' · '}Auto-refreshes every 60 seconds
        </p>
      </div>

      {/* Service cards */}
      <div className="space-y-3">
        {SERVICES.map(service => (
          <div
            key={service.id}
            className={`bg-white rounded-xl border-2 p-4 transition-colors ${
              statuses[service.id] === 'down' ? 'border-red-200' :
              statuses[service.id] === 'degraded' ? 'border-amber-200' :
              statuses[service.id] === 'ok' ? 'border-green-200' :
              'border-gray-200'
            }`}
            data-testid={`service-${service.id}`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-2xl">{service.icon}</span>
                <div>
                  <p className="font-bold text-[#222] text-sm">{service.name}</p>
                  <p className="text-xs text-[#999]">{service.description}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <StatusBadge status={statuses[service.id]} />
                {service.statusPage && (
                  <a
                    href={service.statusPage}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-[#0FA49C] hover:underline flex items-center gap-1"
                  >
                    Status Page <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            </div>
            {details[service.id] && statuses[service.id] !== 'checking' && (
              <p className={`text-xs mt-2 px-11 ${
                statuses[service.id] === 'down' ? 'text-red-600' :
                statuses[service.id] === 'degraded' ? 'text-amber-600' :
                'text-[#999]'
              }`}>
                {details[service.id]}
              </p>
            )}
          </div>
        ))}
      </div>

      {/* Refresh button */}
      <div className="text-center">
        <Button
          variant="outline"
          size="sm"
          onClick={checkAll}
          disabled={checking}
          className="text-xs"
          data-testid="refresh-services-btn"
        >
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${checking ? 'animate-spin' : ''}`} />
          {checking ? 'Checking...' : 'Refresh All'}
        </Button>
      </div>
    </div>
  );
};
